// src/pages/AdminVariantsPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import Loader from "../layouts/components/Loader";
import toast from "react-hot-toast";
import { productVariantsAPI } from "../utils/api";
import "./AdminVariantsPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

// Common cosmetic colors for quick selection
const COMMON_COLORS = {
  'Classic Red': '#DC143C',
  'Cherry Red': '#DE3163',
  'Burgundy': '#800020',
  'Hot Pink': '#FF69B4',
  'Rose Pink': '#F4A6A6',
  'Nude Pink': '#F8BBD9',
  'Coral Pink': '#FF7F7F',
  'Nude': '#F5DEB3',
  'Beige': '#F5F5DC',
  'Peach': '#FFB366',
  'Caramel': '#D2691E',
  'Berry': '#8B008B',
  'Plum': '#DDA0DD',
  'Wine': '#722F37',
  'Brown': '#8B4513',
  'Chocolate': '#7B3F00',
  'Espresso': '#3C2415'
};

export default function AdminVariantsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [variantsLoading, setVariantsLoading] = useState(false);
  
  // Modal states
  const [showAddVariantModal, setShowAddVariantModal] = useState(false);
  const [showEditVariantModal, setShowEditVariantModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [deletingVariant, setDeletingVariant] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form states
  const [variantForm, setVariantForm] = useState({
    color: '',
    hex_color: '',
    images: []
  });
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedColorFilter, setSelectedColorFilter] = useState("");

  const getToken = () =>
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";

  useEffect(() => {
    const token = getToken();
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    fetchProducts(token);
  }, [navigate]);

  const fetchProducts = async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/products/admin`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.products) {
          setProducts(data.products.data || []);
        } else {
          setProducts([]);
        }
      } else {
        setProducts([]);
      }
    } catch (error) {
      toast.error("Error fetching products");
      console.error("Error:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProductVariants = async (productId) => {
    setVariantsLoading(true);
    try {
      const data = await productVariantsAPI.getProduct(productId);
      setVariants(data.product.variants || []);
    } catch (error) {
      console.error('Error loading product variants:', error);
      toast.error('Failed to load product variants');
      setVariants([]);
    } finally {
      setVariantsLoading(false);
    }
  };

  const handleProductSelect = async (product) => {
    setSelectedProduct(product);
    await loadProductVariants(product.id);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    
    setVariantForm(prev => ({
      ...prev,
      [name]: name === 'images' ? Array.from(files) : value
    }));
  };

  const handleColorSelect = (colorName, hexColor) => {
    setVariantForm(prev => ({
      ...prev,
      color: colorName,
      hex_color: hexColor
    }));
  };

  const validateHexColor = (hex) => {
    return /^#[0-9A-Fa-f]{6}$/.test(hex);
  };

  const handleAddVariant = async () => {
    if (!variantForm.color || !variantForm.hex_color || variantForm.images.length === 0) {
      toast.error('Please provide color name, hex color, and at least one image');
      return;
    }

    if (!validateHexColor(variantForm.hex_color)) {
      toast.error('Please enter a valid hex color code (e.g., #FF69B4)');
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      
      formData.append('color', variantForm.color);
      formData.append('hex_color', variantForm.hex_color);
      variantForm.images.forEach((image, index) => {
        formData.append(`images[${index}]`, image);
      });

      await productVariantsAPI.addVariant(selectedProduct.id, formData);

      toast.success('Variant added successfully');
      setShowAddVariantModal(false);
      resetVariantForm();
      await loadProductVariants(selectedProduct.id);
    } catch (error) {
      toast.error(error.message || 'Failed to add variant');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditVariant = (variant) => {
    setEditingVariant(variant);
    setVariantForm({
      color: variant.color || '',
      hex_color: variant.hex_color || '',
      images: []
    });
    setShowEditVariantModal(true);
  };

  const handleUpdateVariant = async () => {
    if (!variantForm.color || !variantForm.hex_color) {
      toast.error('Please provide color name and hex color');
      return;
    }

    if (!validateHexColor(variantForm.hex_color)) {
      toast.error('Please enter a valid hex color code (e.g., #FF69B4)');
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      
      formData.append('color', variantForm.color);
      formData.append('hex_color', variantForm.hex_color);
      
      if (variantForm.images.length > 0) {
        variantForm.images.forEach((image, index) => {
          formData.append(`images[${index}]`, image);
        });
      }

      await productVariantsAPI.updateVariant(editingVariant.id, formData);

      toast.success('Variant updated successfully');
      setShowEditVariantModal(false);
      setEditingVariant(null);
      resetVariantForm();
      await loadProductVariants(selectedProduct.id);
    } catch (error) {
      toast.error(error.message || 'Failed to update variant');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteVariant = (variant) => {
    setDeletingVariant(variant);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingVariant) return;
    
    setIsDeleting(true);
    try {
      await productVariantsAPI.deleteVariant(deletingVariant.id);
      toast.success('Variant deleted successfully');
      setShowDeleteModal(false);
      setDeletingVariant(null);
      await loadProductVariants(selectedProduct.id);
    } catch (error) {
      toast.error(error.message || 'Failed to delete variant');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteImage = async (variantId, imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      setVariantsLoading(true);
      await productVariantsAPI.deleteImage(variantId, imageId);
      toast.success('Image deleted successfully');
      await loadProductVariants(selectedProduct.id);
    } catch (error) {
      toast.error(error.message || 'Failed to delete image');
    } finally {
      setVariantsLoading(false);
    }
  };

  const resetVariantForm = () => {
    setVariantForm({
      color: '',
      hex_color: '',
      images: []
    });
  };

  const openAddVariantModal = () => {
    resetVariantForm();
    setShowAddVariantModal(true);
  };

  const closeAddVariantModal = () => {
    setShowAddVariantModal(false);
    resetVariantForm();
  };

  const closeEditVariantModal = () => {
    setShowEditVariantModal(false);
    setEditingVariant(null);
    resetVariantForm();
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingVariant(null);
    setIsDeleting(false);
  };

  // Filter variants based on search and color filter
  const filteredVariants = variants.filter(variant => {
    const matchesSearch = !searchQuery || 
      variant.color.toLowerCase().includes(searchQuery.toLowerCase()) ||
      variant.hex_color.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesColor = !selectedColorFilter || 
      variant.hex_color.toLowerCase() === selectedColorFilter.toLowerCase();
    
    return matchesSearch && matchesColor;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-variants">
          <div className="loading">Loading products...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="avp-container">
        {/* Header */}
        <div className="avp-header">
          <div className="avp-title">
            <h1>Product Variants Management</h1>
            <p>Manage color variants with hex color codes</p>
          </div>
        </div>

        {/* Product Selection */}
        <div className="avp-product-selection">
          <h2>Select Product</h2>
          <div className="avp-product-grid">
            {products.map((product) => (
              <div 
                key={product.id} 
                className={`avp-product-card ${selectedProduct?.id === product.id ? 'avp-selected' : ''}`}
                onClick={() => handleProductSelect(product)}
              >
                <div className="avp-product-image">
                  {product.images && product.images.length > 0 ? (
                    <img 
                      src={typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url} 
                      alt={product.name}
                    />
                  ) : (
                    <div className="avp-no-image">No Image</div>
                  )}
                </div>
                <div className="avp-product-info">
                  <h3>{product.name}</h3>
                  <p>{product.desc?.substring(0, 50)}...</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Variants Management */}
        {selectedProduct && (
          <div className="avp-variants-management">
            <div className="avp-variants-header">
              <h2>Variants for {selectedProduct.name}</h2>
              <div className="avp-variants-actions">
                <button className="avp-btn-primary" onClick={openAddVariantModal}>
                  <svg viewBox="0 0 24 24">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  Add New Variant
                </button>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="avp-variants-filters">
              <div className="avp-search-input-group">
                <div className="avp-search-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search variants by color or hex code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="avp-search-input"
                />
              </div>
              
              <div className="avp-color-filter">
                <label>Filter by Color:</label>
                <select
                  value={selectedColorFilter}
                  onChange={(e) => setSelectedColorFilter(e.target.value)}
                >
                  <option value="">All Colors</option>
                  {[...new Set(variants.map(v => v.hex_color))].map(hex => (
                    <option key={hex} value={hex}>{hex}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Variants List */}
            {variantsLoading ? (
              <div className="avp-loading">Loading variants...</div>
            ) : (
              <div className="avp-variants-list">
                {filteredVariants.length === 0 ? (
                  <div className="avp-empty-state">
                    <p>No variants found for this product.</p>
                    <button className="avp-btn-primary" onClick={openAddVariantModal}>
                      Add First Variant
                    </button>
                  </div>
                ) : (
                  filteredVariants.map((variant) => (
                    <div key={variant.id} className="avp-variant-card">
                      <div className="avp-variant-header">
                        <div className="avp-variant-info">
                          <h3>{variant.color}</h3>
                          <div className="avp-color-display">
                            <div 
                              className="avp-color-swatch"
                              style={{ backgroundColor: variant.hex_color || '#CCCCCC' }}
                              title={variant.hex_color}
                            />
                            <span className="avp-hex-code">{variant.hex_color}</span>
                          </div>
                        </div>
                        <div className="avp-variant-actions">
                          <button 
                            className="avp-btn-edit"
                            onClick={() => handleEditVariant(variant)}
                            title="Edit Variant"
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" strokeWidth="2" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="currentColor" strokeWidth="2" />
                            </svg>
                          </button>
                          <button 
                            className="avp-btn-delete"
                            onClick={() => handleDeleteVariant(variant)}
                            title="Delete Variant"
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" fill="none" stroke="currentColor" strokeWidth="2" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <div className="avp-variant-images">
                        {variant.images?.map((image) => (
                          <div key={image.id} className="avp-image-container">
                            <img 
                              src={image.url} 
                              alt={`${selectedProduct.name} - ${variant.color}`}
                              className="avp-variant-image"
                            />
                            <button 
                              className="avp-delete-image-btn"
                              onClick={() => handleDeleteImage(variant.id, image.id)}
                              title="Delete image"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Add Variant Modal */}
        {showAddVariantModal && (
          <div className="avp-modal-overlay" onClick={closeAddVariantModal}>
            <div className="avp-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="avp-modal-header">
                <h2>Add New Variant</h2>
                <button className="avp-modal-close" onClick={closeAddVariantModal}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              </div>
              
              <div className="avp-modal-body">
                <div className="avp-form-group">
                  <label>Color Name:</label>
                  <input
                    type="text"
                    name="color"
                    value={variantForm.color}
                    onChange={handleInputChange}
                    placeholder="e.g., Cherry Red, Navy Blue"
                    required
                  />
                </div>
                
                <div className="avp-form-group">
                  <label>Hex Color Code:</label>
                  <div className="avp-color-input-group">
                    <input
                      type="text"
                      name="hex_color"
                      value={variantForm.hex_color}
                      onChange={handleInputChange}
                      placeholder="#FF69B4"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      required
                    />
                    <input
                      type="color"
                      value={variantForm.hex_color || '#CCCCCC'}
                      onChange={(e) => setVariantForm(prev => ({ ...prev, hex_color: e.target.value }))}
                      className="avp-color-picker"
                    />
                  </div>
                </div>

                {/* Quick Color Selection */}
                <div className="avp-form-group">
                  <label>Quick Color Selection:</label>
                  <div className="avp-quick-colors">
                    {Object.entries(COMMON_COLORS).map(([name, hex]) => (
                      <button
                        key={hex}
                        type="button"
                        className="avp-quick-color-btn"
                        style={{ backgroundColor: hex }}
                        onClick={() => handleColorSelect(name, hex)}
                        title={`${name} - ${hex}`}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="avp-form-group">
                  <label>Images:</label>
                  <input
                    type="file"
                    name="images"
                    multiple
                    accept="image/*"
                    onChange={handleInputChange}
                    required
                  />
                  {variantForm.images.length > 0 && (
                    <div className="avp-image-previews">
                      {variantForm.images.map((image, index) => (
                        <div key={index} className="avp-image-preview">
                          <img src={URL.createObjectURL(image)} alt={`Preview ${index + 1}`} />
                          <span>{image.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="avp-modal-footer">
                <button 
                  className="avp-btn-secondary"
                  onClick={closeAddVariantModal}
                >
                  Cancel
                </button>
                <button 
                  className="avp-btn-primary"
                  onClick={handleAddVariant}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader size={16} strokeWidth={2} />
                      Adding...
                    </>
                  ) : (
                    'Add Variant'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Variant Modal */}
        {showEditVariantModal && (
          <div className="avp-modal-overlay" onClick={closeEditVariantModal}>
            <div className="avp-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="avp-modal-header">
                <h2>Edit Variant</h2>
                <button className="avp-modal-close" onClick={closeEditVariantModal}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              </div>
              
              <div className="avp-modal-body">
                <div className="avp-form-group">
                  <label>Color Name:</label>
                  <input
                    type="text"
                    name="color"
                    value={variantForm.color}
                    onChange={handleInputChange}
                    placeholder="e.g., Cherry Red, Navy Blue"
                    required
                  />
                </div>
                
                <div className="avp-form-group">
                  <label>Hex Color Code:</label>
                  <div className="avp-color-input-group">
                    <input
                      type="text"
                      name="hex_color"
                      value={variantForm.hex_color}
                      onChange={handleInputChange}
                      placeholder="#FF69B4"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      required
                    />
                    <input
                      type="color"
                      value={variantForm.hex_color || '#CCCCCC'}
                      onChange={(e) => setVariantForm(prev => ({ ...prev, hex_color: e.target.value }))}
                      className="avp-color-picker"
                    />
                  </div>
                </div>

                {/* Quick Color Selection */}
                <div className="avp-form-group">
                  <label>Quick Color Selection:</label>
                  <div className="avp-quick-colors">
                    {Object.entries(COMMON_COLORS).map(([name, hex]) => (
                      <button
                        key={hex}
                        type="button"
                        className="avp-quick-color-btn"
                        style={{ backgroundColor: hex }}
                        onClick={() => handleColorSelect(name, hex)}
                        title={`${name} - ${hex}`}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="avp-form-group">
                  <label>Add New Images (optional):</label>
                  <input
                    type="file"
                    name="images"
                    multiple
                    accept="image/*"
                    onChange={handleInputChange}
                  />
                  {variantForm.images.length > 0 && (
                    <div className="avp-image-previews">
                      {variantForm.images.map((image, index) => (
                        <div key={index} className="avp-image-preview">
                          <img src={URL.createObjectURL(image)} alt={`Preview ${index + 1}`} />
                          <span>{image.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="avp-modal-footer">
                <button 
                  className="avp-btn-secondary"
                  onClick={closeEditVariantModal}
                >
                  Cancel
                </button>
                <button 
                  className="avp-btn-primary"
                  onClick={handleUpdateVariant}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader size={16} strokeWidth={2} />
                      Updating...
                    </>
                  ) : (
                    'Update Variant'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title="Delete Variant"
          message="Are you sure you want to delete this variant? This action cannot be undone."
          itemName={deletingVariant ? `${deletingVariant.color} (${deletingVariant.hex_color})` : ""}
          isLoading={isDeleting}
        />
      </div>
    </AdminLayout>
  );
}
